import React, { useState, useEffect } from 'react';
import { api } from '../api';
import { Cluster, Cantiere } from '../types';
import ActionMenu from '../components/ActionMenu';
import { useNotification } from '../components/NotificationContext';

const AdminClusters: React.FC = () => {
  const [clusters, setClusters] = useState<Cluster[]>([]);
  const [cantieri, setCantieri] = useState<Cantiere[]>([]);
  const [editingCluster, setEditingCluster] = useState<Cluster | null>(null);
  const { notify } = useNotification();

  const initialFormState = {
    name: '',
    description: '',
    expected_images: 0,
    cantieri: [] as number[], // Array of cantiere IDs
  };

  const [formData, setFormData] = useState(initialFormState);

  useEffect(() => {
    fetchClusters();
    fetchCantieri();
  }, []);

  const fetchClusters = async () => {
    try {
      const data = await api.get('/admin/clusters');
      setClusters(data.clusters || []);
    } catch (error: any) {
      notify(error.message || 'Errore nel caricamento dei cluster.', 'error');
    }
  };

  const fetchCantieri = async () => {
    try {
      const data = await api.get('/admin/cantieri');
      setCantieri(data.cantieri || []);
    } catch (error: any) {
      notify(error.message || 'Errore nel caricamento dei cantieri.', 'error');
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    const val = type === 'number' ? parseInt(value, 10) || 0 : value;
    setFormData({ ...formData, [name]: val });
  };

  const handleCantiereCheckboxChange = (cantiereId: number, checked: boolean) => {
    let updatedCantieri = [...formData.cantieri];
    if (checked) {
      if (!updatedCantieri.includes(cantiereId)) {
        updatedCantieri.push(cantiereId);
      }
    } else {
      updatedCantieri = updatedCantieri.filter(id => id !== cantiereId);
    }
    setFormData({ ...formData, cantieri: updatedCantieri });
  };

  const handleEdit = (cluster: Cluster) => {
    setEditingCluster(cluster);
    setFormData({
      name: cluster.name,
      description: cluster.description || '',
      expected_images: cluster.expected_images || 0,
      cantieri: cluster.cantieri.map(c => c.id),
    });
  };

  const handleDelete = async (clusterId: number) => {
    if (window.confirm('Sei sicuro di voler eliminare questo cluster? I cantieri associati non verranno eliminati.')) {
      try {
        await api.delete(`/admin/clusters/${clusterId}`);
        notify('Cluster eliminato con successo!', 'success');
        fetchClusters();
      } catch (error: any) {
        notify(error.message || 'Errore durante l\'eliminazione del cluster.', 'error');
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (editingCluster) {
        await api.put(`/admin/clusters/${editingCluster.id}`, formData);
        notify('Cluster aggiornato con successo!', 'success');
      } else {
        await api.post('/admin/clusters', formData);
        notify('Cluster creato con successo!', 'success');
      }
      setFormData(initialFormState);
      setEditingCluster(null);
      fetchClusters();
    } catch (error: any) {
      notify(error.message || 'Errore durante il salvataggio del cluster.', 'error');
    }
  };

  return (
    <div className="container">
      <div className="card">
        <h2 style={{ marginBottom: '24px' }}>{editingCluster ? 'Modifica Cluster' : 'Nuovo Cluster'}</h2>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Nome Cluster</label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              required
            />
          </div>

          <div className="form-group">
            <label>Descrizione</label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              rows={3}
            />
          </div>

          <div className="form-group">
            <label>Immagini totali previste</label>
            <input
              type="number"
              name="expected_images"
              value={formData.expected_images}
              onChange={handleInputChange}
              min="0"
            />
          </div>

          <div className="form-group">
            <label style={{ marginBottom: '8px', display: 'block' }}>Cantieri appartenenti al cluster</label>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
              gap: '12px',
              maxHeight: '180px',
              overflowY: 'auto',
              border: '1px solid #ddd',
              padding: '12px',
              borderRadius: '6px',
              background: '#fafafa'
            }}>
              {cantieri.map(c => (
                <label key={c.id} style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '0.9rem' }}>
                  <input
                    type="checkbox"
                    checked={formData.cantieri.includes(c.id)}
                    onChange={(e) => handleCantiereCheckboxChange(c.id, e.target.checked)}
                  />
                  <span>{c.name}</span>
                </label>
              ))}
              {cantieri.length === 0 && <span style={{ color: '#999', fontSize: '0.9rem' }}>Nessun cantiere disponibile</span>}
            </div>
          </div>

          <div style={{ display: 'flex', gap: '12px', marginTop: '24px' }}>
            <button type="submit" className="btn btn-primary">
              {editingCluster ? 'Salva Modifiche' : 'Crea Cluster'}
            </button>
            {editingCluster && (
              <button
                type="button"
                className="btn btn-text"
                onClick={() => { setEditingCluster(null); setFormData(initialFormState); }}
              >
                Annulla
              </button>
            )}
          </div>
        </form>
      </div>

      <div className="card" style={{ marginTop: '24px' }}>
        <h3>Lista Cluster</h3>
        <table className="table-responsive">
          <thead>
            <tr>
              <th>Nome</th>
              <th>Descrizione</th>
              <th>Immagini previste</th>
              <th>Cantieri associati</th>
              <th>Azioni</th>
            </tr>
          </thead>
          <tbody>
            {clusters.map((cluster) => (
              <tr key={cluster.id}>
                <td><strong>{cluster.name}</strong></td>
                <td>{cluster.description || <span style={{ opacity: 0.4 }}>-</span>}</td>
                <td>{cluster.expected_images.toLocaleString()}</td>
                <td>
                  <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                    {cluster.cantieri.map(c => (
                      <span key={c.id} style={{
                        fontSize: '0.75rem',
                        background: '#e0f2f1',
                        color: '#00695c',
                        padding: '2px 6px',
                        borderRadius: '4px',
                        fontWeight: '500'
                      }}>{c.name}</span>
                    ))}
                    {cluster.cantieri.length === 0 && <span style={{ opacity: 0.4 }}>Nessun cantiere</span>}
                  </div>
                </td>
                <td>
                  <ActionMenu actions={[
                    { label: 'Modifica', icon: 'fas fa-pen-to-square', onClick: () => handleEdit(cluster) },
                    { label: 'Elimina', icon: 'fas fa-trash-can', onClick: () => handleDelete(cluster.id), color: 'var(--error-color)' },
                  ]} />
                </td>
              </tr>
            ))}
            {clusters.length === 0 && (
              <tr>
                <td colSpan={5} style={{ textAlign: 'center', padding: '20px', color: '#999' }}>
                  Nessun cluster configurato.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AdminClusters;
